import {
  Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy, HostListener
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fabric } from 'fabric';
import { DesignService, Design } from '../services/design.service';
import { AuthService } from '../components/services/auth.service';

// ── Architectural symbol library ─────────────────────────────────────────────
// All symbols follow AIA/ANSI floor-plan drafting conventions:
//  - Pure black strokes on white, no decorative color
//  - Correct geometry for door swings (quarter-circle arcs), window triple-lines,
//    fixture proportions, and standard electrical symbols
const SVGS: Record<string, string> = {

  // ══ DOORS ═══════════════════════════════════════════════════════════════════
  // Convention: wall stubs (filled rect) on each side of opening; door panel
  // (thin filled rect) in closed position; dashed quarter-circle swing arc.

  'door-r': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 84 78">
    <rect x="0"  y="0" width="8" height="6" fill="#000"/>
    <rect x="76" y="0" width="8" height="6" fill="#000"/>
    <rect x="8"  y="0" width="68" height="4" fill="#000"/>
    <path d="M 76,4 A 68,68 0 0,1 8,72" fill="none" stroke="#000" stroke-width="1.5" stroke-dasharray="5,3"/>
    <circle cx="8" cy="4" r="2.5" fill="#000"/>
  </svg>`,

  'door-l': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 84 78">
    <rect x="0"  y="0" width="8" height="6" fill="#000"/>
    <rect x="76" y="0" width="8" height="6" fill="#000"/>
    <rect x="8"  y="0" width="68" height="4" fill="#000"/>
    <path d="M 8,4 A 68,68 0 0,0 76,72" fill="none" stroke="#000" stroke-width="1.5" stroke-dasharray="5,3"/>
    <circle cx="76" cy="4" r="2.5" fill="#000"/>
  </svg>`,

  'door-double': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 64">
    <rect x="0"   y="0" width="8" height="6" fill="#000"/>
    <rect x="112" y="0" width="8" height="6" fill="#000"/>
    <rect x="8"   y="0" width="52" height="4" fill="#000"/>
    <rect x="60"  y="0" width="52" height="4" fill="#000"/>
    <path d="M 8,4 A 52,52 0 0,0 60,56"  fill="none" stroke="#000" stroke-width="1.5" stroke-dasharray="5,3"/>
    <path d="M 112,4 A 52,52 0 0,1 60,56" fill="none" stroke="#000" stroke-width="1.5" stroke-dasharray="5,3"/>
    <circle cx="60" cy="4" r="2.5" fill="#000"/>
  </svg>`,

  'door-sliding': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 28">
    <line x1="0" y1="1"  x2="120" y2="1"  stroke="#000" stroke-width="2.5"/>
    <line x1="0" y1="27" x2="120" y2="27" stroke="#000" stroke-width="2.5"/>
    <rect x="2"  y="1" width="68" height="26" fill="white" stroke="#000" stroke-width="2"/>
    <rect x="50" y="1" width="68" height="26" fill="white" stroke="#000" stroke-width="1.5" stroke-dasharray="4,3"/>
    <line x1="12" y1="14" x2="58" y2="14" stroke="#000" stroke-width="1"/>
    <polygon points="52,10 64,14 52,18" fill="#000"/>
    <polygon points="68,10 56,14 68,18" fill="#000"/>
  </svg>`,

  'door-pocket': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 28">
    <line x1="0" y1="1"  x2="120" y2="1"  stroke="#000" stroke-width="3"/>
    <line x1="0" y1="27" x2="120" y2="27" stroke="#000" stroke-width="3"/>
    <rect x="4"  y="4" width="54" height="20" fill="white" stroke="#000" stroke-width="1.5" stroke-dasharray="4,3"/>
    <rect x="58" y="1" width="60" height="26" fill="white" stroke="#000" stroke-width="2"/>
    <line x1="14" y1="14" x2="56" y2="14" stroke="#000" stroke-width="1"/>
    <polygon points="52,10 62,14 52,18" fill="#000"/>
  </svg>`,

  'door-garage': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 28">
    <rect x="0" y="0" width="160" height="28" fill="white" stroke="#000" stroke-width="3"/>
    <line x1="0" y1="9"  x2="160" y2="9"  stroke="#000" stroke-width="1.5"/>
    <line x1="0" y1="18" x2="160" y2="18" stroke="#000" stroke-width="1.5"/>
    <line x1="32"  y1="0" x2="32"  y2="28" stroke="#000" stroke-width="1"/>
    <line x1="64"  y1="0" x2="64"  y2="28" stroke="#000" stroke-width="1"/>
    <line x1="96"  y1="0" x2="96"  y2="28" stroke="#000" stroke-width="1"/>
    <line x1="128" y1="0" x2="128" y2="28" stroke="#000" stroke-width="1"/>
  </svg>`,

  // ══ WINDOWS ═══════════════════════════════════════════════════════════════
  // Convention: two thick outer glass lines + one center air-gap line,
  // wall stubs (filled rect) on each side.

  'window': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 22">
    <rect x="0"  y="0" width="8"  height="22" fill="#000"/>
    <rect x="92" y="0" width="8"  height="22" fill="#000"/>
    <line x1="8" y1="4"  x2="92" y2="4"  stroke="#000" stroke-width="2"/>
    <line x1="8" y1="11" x2="92" y2="11" stroke="#000" stroke-width="1.5"/>
    <line x1="8" y1="18" x2="92" y2="18" stroke="#000" stroke-width="2"/>
    <rect x="8" y="4" width="84" height="14" fill="rgba(200,230,255,0.25)"/>
  </svg>`,

  'window-bay': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130 68">
    <rect x="0"   y="0" width="8" height="8" fill="#000"/>
    <rect x="122" y="0" width="8" height="8" fill="#000"/>
    <line x1="8"   y1="2" x2="36"  y2="54" stroke="#000" stroke-width="2.5"/>
    <line x1="122" y1="2" x2="94"  y2="54" stroke="#000" stroke-width="2.5"/>
    <line x1="36"  y1="54" x2="94" y2="54" stroke="#000" stroke-width="2.5"/>
    <line x1="12"  y1="8"  x2="37" y2="48" stroke="#000" stroke-width="1.5"/>
    <line x1="16"  y1="8"  x2="37" y2="40" stroke="#000" stroke-width="1"/>
    <line x1="118" y1="8"  x2="93" y2="48" stroke="#000" stroke-width="1.5"/>
    <line x1="114" y1="8"  x2="93" y2="40" stroke="#000" stroke-width="1"/>
    <line x1="37"  y1="57" x2="93" y2="57" stroke="#000" stroke-width="1.5"/>
    <line x1="37"  y1="61" x2="93" y2="61" stroke="#000" stroke-width="1"/>
  </svg>`,

  // ══ STAIRS & VERTICAL ════════════════════════════════════════════════════
  // Convention: parallel tread lines, break line at mid-flight, directional arrow.

  'stairs-up': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 70 130">
    <rect x="2" y="2" width="66" height="126" fill="white" stroke="#000" stroke-width="2.5"/>
    <line x1="2" y1="15"  x2="68" y2="15"  stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="28"  x2="68" y2="28"  stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="41"  x2="68" y2="41"  stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="54"  x2="68" y2="54"  stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="67"  x2="68" y2="67"  stroke="#000" stroke-width="2.5"/>
    <line x1="8" y1="62"  x2="16" y2="62"  stroke="#000" stroke-width="1.5"/>
    <line x1="4" y1="67"  x2="20" y2="67"  stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="80"  x2="68" y2="80"  stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="93"  x2="68" y2="93"  stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="106" x2="68" y2="106" stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="119" x2="68" y2="119" stroke="#000" stroke-width="1.5"/>
    <line x1="35" y1="115" x2="35" y2="20" stroke="#000" stroke-width="1.5"/>
    <polygon points="28,32 35,18 42,32" fill="#000"/>
    <text x="35" y="128" font-family="Arial" font-size="9" text-anchor="middle" fill="#000">UP</text>
  </svg>`,

  'stairs-l': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect x="2" y="2" width="48" height="96" fill="white" stroke="#000" stroke-width="2.5"/>
    <line x1="2" y1="14" x2="50" y2="14" stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="26" x2="50" y2="26" stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="38" x2="50" y2="38" stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="50" x2="50" y2="50" stroke="#000" stroke-width="2.5"/>
    <line x1="6" y1="46" x2="14" y2="46" stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="50" x2="18" y2="50" stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="62" x2="50" y2="62" stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="74" x2="50" y2="74" stroke="#000" stroke-width="1.5"/>
    <line x1="2" y1="86" x2="50" y2="86" stroke="#000" stroke-width="1.5"/>
    <rect x="50" y="50" width="48" height="48" fill="white" stroke="#000" stroke-width="2.5"/>
    <line x1="62" y1="50" x2="62" y2="98" stroke="#000" stroke-width="1.5"/>
    <line x1="74" y1="50" x2="74" y2="98" stroke="#000" stroke-width="1.5"/>
    <line x1="86" y1="50" x2="86" y2="98" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'elevator': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
    <rect x="2" y="2" width="76" height="76" fill="white" stroke="#000" stroke-width="2.5"/>
    <line x1="40" y1="2" x2="40" y2="78" stroke="#000" stroke-width="1" stroke-dasharray="4,3"/>
    <polygon points="33,22 40,10 47,22" fill="#000"/>
    <polygon points="33,58 40,70 47,58" fill="#000"/>
    <rect x="2" y="72" width="20" height="6" fill="#000"/>
    <line x1="22" y1="75" x2="32" y2="75" stroke="#000" stroke-width="2.5"/>
  </svg>`,

  'ramp': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 72">
    <rect x="2" y="2" width="96" height="68" fill="white" stroke="#000" stroke-width="2.5"/>
    <line x1="8" y1="66" x2="92" y2="6" stroke="#000" stroke-width="1.5"/>
    <polygon points="80,4 96,2 96,20" fill="none" stroke="#000" stroke-width="1.5"/>
    <text x="30" y="55" font-family="Arial" font-size="9" text-anchor="middle" fill="#000" transform="rotate(-30,30,55)">RAMP</text>
  </svg>`,

  // ══ PLUMBING ═════════════════════════════════════════════════════════════

  'toilet': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 84">
    <rect x="7" y="2" width="44" height="18" rx="2" fill="white" stroke="#000" stroke-width="2"/>
    <path d="M 9,20 L 9,52 Q 9,82 29,82 Q 49,82 49,52 L 49,20 Z"
          fill="white" stroke="#000" stroke-width="2"/>
    <path d="M 13,22 L 13,52 Q 13,76 29,76 Q 45,76 45,52 L 45,22 Z"
          fill="none" stroke="#000" stroke-width="1.5"/>
    <path d="M 18,26 L 18,52 Q 18,70 29,70 Q 40,70 40,52 L 40,26 Z"
          fill="rgba(200,230,255,0.25)" stroke="#000" stroke-width="1"/>
    <circle cx="46" cy="11" r="3" fill="#000"/>
  </svg>`,

  'sink-round': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60">
    <rect x="2" y="2" width="56" height="56" rx="4" fill="white" stroke="#000" stroke-width="2.5"/>
    <circle cx="30" cy="30" r="21" fill="rgba(200,230,255,0.2)" stroke="#000" stroke-width="1.5"/>
    <circle cx="30" cy="30" r="4" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="30" cy="12" r="3.5" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="30" y1="15.5" x2="30" y2="21" stroke="#000" stroke-width="1.5"/>
    <line x1="25" y1="21" x2="35" y2="21" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'sink-rect': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 54">
    <rect x="2" y="2" width="60" height="50" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="8" y="8" width="48" height="36" rx="8" fill="rgba(200,230,255,0.2)" stroke="#000" stroke-width="1.5"/>
    <circle cx="32" cy="34" r="4" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="32" cy="14" r="3" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="32" y1="17" x2="32" y2="22" stroke="#000" stroke-width="1.5"/>
    <line x1="27" y1="22" x2="37" y2="22" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'bathtub': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 60">
    <rect x="2" y="2" width="110" height="56" rx="6" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="10" y="8" width="86" height="44" rx="10" fill="rgba(200,230,255,0.2)" stroke="#000" stroke-width="1.5"/>
    <circle cx="22" cy="30" r="8" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="22" cy="30" r="2" fill="#000"/>
    <circle cx="98" cy="22" r="3" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="98" cy="38" r="3" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="98" y1="22" x2="96" y2="30" stroke="#000" stroke-width="1.5"/>
    <line x1="98" y1="38" x2="96" y2="30" stroke="#000" stroke-width="1.5"/>
    <circle cx="96" cy="30" r="2.5" fill="#000"/>
  </svg>`,

  'shower': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 82 82">
    <rect x="2" y="2" width="78" height="78" fill="white" stroke="#000" stroke-width="2.5"/>
    <path d="M 2,32 A 30,30 0 0,1 32,2" fill="none" stroke="#000" stroke-width="1.5" stroke-dasharray="4,3"/>
    <circle cx="62" cy="62" r="7" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="62" cy="62" r="2.5" fill="#000"/>
    <circle cx="15" cy="15" r="6" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="13" y1="13" x2="10" y2="10" stroke="#000" stroke-width="1"/>
    <line x1="15" y1="11" x2="15" y2="8"  stroke="#000" stroke-width="1"/>
    <line x1="17" y1="13" x2="20" y2="10" stroke="#000" stroke-width="1"/>
    <line x1="19" y1="15" x2="22" y2="15" stroke="#000" stroke-width="1"/>
    <line x1="17" y1="17" x2="20" y2="20" stroke="#000" stroke-width="1"/>
    <line x1="15" y1="19" x2="15" y2="22" stroke="#000" stroke-width="1"/>
  </svg>`,

  'urinal': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 54 72">
    <rect x="5" y="2" width="44" height="10" rx="2" fill="white" stroke="#000" stroke-width="2"/>
    <path d="M 9,12 L 9,46 Q 9,68 27,68 Q 45,68 45,46 L 45,12 Z"
          fill="white" stroke="#000" stroke-width="2"/>
    <path d="M 14,16 L 14,46 Q 14,62 27,62 Q 40,62 40,46 L 40,16 Z"
          fill="rgba(200,230,255,0.2)" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  // ══ KITCHEN ══════════════════════════════════════════════════════════════

  'fridge': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 58 90">
    <rect x="2" y="2" width="54" height="86" rx="4" fill="white" stroke="#000" stroke-width="2.5"/>
    <line x1="2" y1="32" x2="56" y2="32" stroke="#000" stroke-width="2"/>
    <line x1="14" y1="10" x2="14" y2="28" stroke="#000" stroke-width="3" stroke-linecap="round"/>
    <line x1="14" y1="42" x2="14" y2="78" stroke="#000" stroke-width="3" stroke-linecap="round"/>
    <circle cx="50" cy="17" r="2.5" fill="#000"/>
    <circle cx="50" cy="60" r="2.5" fill="#000"/>
  </svg>`,

  'range': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80">
    <rect x="2" y="2" width="76" height="76" fill="white" stroke="#000" stroke-width="2.5"/>
    <circle cx="23" cy="23" r="13" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="23" cy="23" r="7"  fill="none" stroke="#000" stroke-width="1"/>
    <circle cx="57" cy="23" r="13" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="57" cy="23" r="7"  fill="none" stroke="#000" stroke-width="1"/>
    <circle cx="23" cy="57" r="13" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="23" cy="57" r="7"  fill="none" stroke="#000" stroke-width="1"/>
    <circle cx="57" cy="57" r="13" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="57" cy="57" r="7"  fill="none" stroke="#000" stroke-width="1"/>
    <circle cx="29" cy="17" r="2" fill="#000"/>
    <circle cx="63" cy="17" r="2" fill="#000"/>
    <circle cx="29" cy="51" r="2" fill="#000"/>
    <circle cx="63" cy="51" r="2" fill="#000"/>
  </svg>`,

  'dishwasher': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60">
    <rect x="2" y="2" width="56" height="56" fill="white" stroke="#000" stroke-width="2.5"/>
    <line x1="14" y1="8" x2="14" y2="52" stroke="#000" stroke-width="3" stroke-linecap="round"/>
    <rect x="18" y="6" width="34" height="10" rx="2" fill="none" stroke="#000" stroke-width="1"/>
    <circle cx="24" cy="11" r="2" fill="#000"/>
    <circle cx="32" cy="11" r="2" fill="#000"/>
    <circle cx="40" cy="11" r="2" fill="none" stroke="#000" stroke-width="1"/>
  </svg>`,

  'sink-kitchen': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 104 60">
    <rect x="2" y="2" width="100" height="56" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="6"  y="6" width="42" height="46" rx="4" fill="rgba(200,230,255,0.2)" stroke="#000" stroke-width="1.5"/>
    <rect x="56" y="6" width="42" height="46" rx="4" fill="rgba(200,230,255,0.2)" stroke="#000" stroke-width="1.5"/>
    <line x1="52" y1="2" x2="52" y2="58" stroke="#000" stroke-width="1.5"/>
    <circle cx="27" cy="38" r="4" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="77" cy="38" r="4" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="52" cy="14" r="4.5" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="52" y1="18.5" x2="52" y2="26" stroke="#000" stroke-width="1.5"/>
    <line x1="43" y1="26" x2="61" y2="26" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'counter': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130 30">
    <rect x="0" y="0" width="130" height="30" fill="white" stroke="#000" stroke-width="3"/>
    <rect x="3" y="3" width="124" height="24" fill="none" stroke="#000" stroke-width="1"/>
  </svg>`,

  'island': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 110 68">
    <rect x="0" y="0" width="110" height="68" rx="4" fill="white" stroke="#000" stroke-width="3"/>
    <rect x="4" y="4" width="102" height="60" rx="2" fill="none" stroke="#000" stroke-width="1"/>
  </svg>`,

  // ══ ELECTRICAL ═══════════════════════════════════════════════════════════
  // Standard architectural electrical plan symbols (AIA conventions).

  'outlet': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36">
    <circle cx="18" cy="18" r="16" fill="white" stroke="#000" stroke-width="2"/>
    <rect x="14" y="8"  width="3" height="8" rx="1" fill="#000"/>
    <rect x="19" y="8"  width="3" height="8" rx="1" fill="#000"/>
    <circle cx="18" cy="25" r="3" fill="none" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'outlet-gfi': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 46">
    <rect x="2" y="2" width="34" height="42" rx="4" fill="white" stroke="#000" stroke-width="2"/>
    <rect x="13" y="6"  width="3" height="8" rx="1" fill="#000"/>
    <rect x="22" y="6"  width="3" height="8" rx="1" fill="#000"/>
    <rect x="8"  y="18" width="10" height="8" rx="1" fill="none" stroke="#000" stroke-width="1.5"/>
    <rect x="20" y="18" width="10" height="8" rx="1" fill="none" stroke="#000" stroke-width="1.5"/>
    <text x="13" y="24.5" font-family="Arial" font-size="5" text-anchor="middle" fill="#000">RES</text>
    <text x="25" y="24.5" font-family="Arial" font-size="5" text-anchor="middle" fill="#000">TST</text>
    <circle cx="19" cy="36" r="3.5" fill="none" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'switch': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 40">
    <rect x="2" y="2" width="26" height="36" rx="4" fill="white" stroke="#000" stroke-width="2"/>
    <rect x="8" y="8" width="14" height="14" rx="2" fill="none" stroke="#000" stroke-width="1.5"/>
    <text x="15" y="18.5" font-family="Arial" font-size="9" font-weight="bold" text-anchor="middle" fill="#000">S</text>
  </svg>`,

  'light-ceiling': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
    <circle cx="26" cy="26" r="24" fill="white" stroke="#000" stroke-width="2"/>
    <circle cx="26" cy="26" r="11" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="19" y1="19" x2="33" y2="33" stroke="#000" stroke-width="1.5"/>
    <line x1="33" y1="19" x2="19" y2="33" stroke="#000" stroke-width="1.5"/>
    <circle cx="26" cy="26" r="3" fill="#000"/>
  </svg>`,

  'light-recessed': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="22" fill="white" stroke="#000" stroke-width="2"/>
    <circle cx="24" cy="24" r="14" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="24" cy="24" r="6"  fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="24" cy="24" r="2.5" fill="#000"/>
  </svg>`,

  'fan': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 66">
    <circle cx="33" cy="33" r="31" fill="white" stroke="#000" stroke-width="1.5" stroke-dasharray="4,3"/>
    <circle cx="33" cy="33" r="5" fill="#000"/>
    <path d="M 33,28 Q 37,14 29,6  Q 21,14 23,28 Z" fill="none" stroke="#000" stroke-width="1.5"/>
    <path d="M 38,33 Q 52,29 60,37 Q 52,45 38,43 Z" fill="none" stroke="#000" stroke-width="1.5"/>
    <path d="M 33,38 Q 29,52 37,60 Q 45,52 43,38 Z" fill="none" stroke="#000" stroke-width="1.5"/>
    <path d="M 28,33 Q 14,37 6,29  Q 14,21 28,23 Z" fill="none" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'smoke': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 42 46">
    <circle cx="21" cy="21" r="19" fill="white" stroke="#000" stroke-width="2"/>
    <circle cx="21" cy="21" r="11" fill="none" stroke="#000" stroke-width="1.5"/>
    <circle cx="21" cy="21" r="3.5" fill="#000"/>
    <text x="21" y="44" font-family="Arial" font-size="7" font-weight="bold" text-anchor="middle" fill="#000">SD</text>
  </svg>`,

  // ══ RESIDENTIAL FURNITURE ════════════════════════════════════════════════

  'bed-twin': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 74 118">
    <rect x="2" y="22" width="70" height="94" rx="5" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="2" y="2"  width="70" height="22" rx="3" fill="none" stroke="#000" stroke-width="2.5"/>
    <rect x="8" y="26" width="58" height="24" rx="8" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="2"  y1="62" x2="72" y2="62"  stroke="#000" stroke-width="0.75"/>
    <line x1="2"  y1="80" x2="72" y2="80"  stroke="#000" stroke-width="0.75"/>
    <line x1="2"  y1="98" x2="72" y2="98"  stroke="#000" stroke-width="0.75"/>
    <line x1="25" y1="62" x2="25" y2="116" stroke="#000" stroke-width="0.75"/>
    <line x1="49" y1="62" x2="49" y2="116" stroke="#000" stroke-width="0.75"/>
  </svg>`,

  'bed-queen': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 110 138">
    <rect x="2" y="24" width="106" height="112" rx="5" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="2" y="2"  width="106" height="24" rx="3" fill="none" stroke="#000" stroke-width="2.5"/>
    <rect x="8"  y="30" width="42" height="26" rx="7" fill="none" stroke="#000" stroke-width="1.5"/>
    <rect x="60" y="30" width="42" height="26" rx="7" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="2"  y1="70" x2="108" y2="70"  stroke="#000" stroke-width="0.75"/>
    <line x1="2"  y1="98" x2="108" y2="98"  stroke="#000" stroke-width="0.75"/>
    <line x1="2"  y1="126" x2="108" y2="126" stroke="#000" stroke-width="0.75"/>
    <line x1="37" y1="70"  x2="37"  y2="136" stroke="#000" stroke-width="0.75"/>
    <line x1="73" y1="70"  x2="73"  y2="136" stroke="#000" stroke-width="0.75"/>
  </svg>`,

  'bed-king': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 152 138">
    <rect x="2" y="24" width="148" height="112" rx="5" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="2" y="2"  width="148" height="24" rx="3" fill="none" stroke="#000" stroke-width="2.5"/>
    <rect x="8"  y="30" width="64" height="26" rx="7" fill="none" stroke="#000" stroke-width="1.5"/>
    <rect x="80" y="30" width="64" height="26" rx="7" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="2"  y1="70" x2="150" y2="70"  stroke="#000" stroke-width="0.75"/>
    <line x1="2"  y1="98" x2="150" y2="98"  stroke="#000" stroke-width="0.75"/>
    <line x1="2"  y1="126" x2="150" y2="126" stroke="#000" stroke-width="0.75"/>
    <line x1="52" y1="70"  x2="52"  y2="136" stroke="#000" stroke-width="0.75"/>
    <line x1="100" y1="70" x2="100" y2="136" stroke="#000" stroke-width="0.75"/>
  </svg>`,

  'sofa': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 148 74">
    <rect x="14"  y="2"  width="120" height="26" rx="6" fill="none" stroke="#000" stroke-width="2"/>
    <rect x="2"   y="2"  width="14"  height="70" rx="5" fill="none" stroke="#000" stroke-width="2"/>
    <rect x="132" y="2"  width="14"  height="70" rx="5" fill="none" stroke="#000" stroke-width="2"/>
    <rect x="16"  y="28" width="38"  height="44" rx="4" fill="none" stroke="#000" stroke-width="1.5"/>
    <rect x="55"  y="28" width="38"  height="44" rx="4" fill="none" stroke="#000" stroke-width="1.5"/>
    <rect x="94"  y="28" width="38"  height="44" rx="4" fill="none" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'chair': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 64">
    <rect x="4" y="2"  width="52" height="16" rx="5" fill="none" stroke="#000" stroke-width="2"/>
    <rect x="4" y="18" width="52" height="44" rx="4" fill="none" stroke="#000" stroke-width="2"/>
    <line x1="4"  y1="38" x2="56" y2="38" stroke="#000" stroke-width="0.75"/>
    <line x1="27" y1="18" x2="27" y2="62" stroke="#000" stroke-width="0.75"/>
  </svg>`,

  'desk': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 70">
    <rect x="2" y="2" width="86" height="66" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="2" y="2" width="34" height="30" fill="none" stroke="#000" stroke-width="1.5"/>
    <rect x="40" y="10" width="40" height="26" rx="2" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="57" y1="36" x2="57" y2="44" stroke="#000" stroke-width="1.5"/>
    <line x1="51" y1="44" x2="63" y2="44" stroke="#000" stroke-width="1.5"/>
    <rect x="40" y="48" width="40" height="14" rx="1" fill="none" stroke="#000" stroke-width="1"/>
  </svg>`,

  'table-round': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 86 86">
    <circle cx="43" cy="43" r="38" fill="white" stroke="#000" stroke-width="2.5"/>
    <path d="M 30,5  A 15,8 0 0,1 56,5"  fill="none" stroke="#000" stroke-width="3" stroke-linecap="round"/>
    <path d="M 30,81 A 15,8 0 0,0 56,81" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round"/>
    <path d="M 5,30  A 8,15 0 0,0 5,56"  fill="none" stroke="#000" stroke-width="3" stroke-linecap="round"/>
    <path d="M 81,30 A 8,15 0 0,1 81,56" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round"/>
    <path d="M 8,14  A 8,12 30 0,0 14,8" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round"/>
    <path d="M 72,78 A 8,12 30 0,0 78,72" fill="none" stroke="#000" stroke-width="3" stroke-linecap="round"/>
  </svg>`,

  'fireplace': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 92 60">
    <rect x="2" y="2" width="88" height="56" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="14" y="10" width="64" height="38" fill="#1a1a1a" stroke="#000" stroke-width="2"/>
    <line x1="2"  y1="48" x2="90" y2="48" stroke="#000" stroke-width="1.5"/>
    <path d="M 46,44 Q 40,34 44,24 Q 48,34 46,44" fill="none" stroke="#555" stroke-width="1.5"/>
    <path d="M 46,44 Q 53,28 57,34 Q 53,38 46,44" fill="none" stroke="#555" stroke-width="1.5"/>
  </svg>`,

  // ══ COMMERCIAL ═══════════════════════════════════════════════════════════

  'office-desk': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 94 74">
    <rect x="2" y="2" width="90" height="70" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="2" y="2" width="36" height="32" fill="none" stroke="#000" stroke-width="1.5"/>
    <rect x="42" y="8" width="42" height="28" rx="2" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="60" y1="36" x2="60" y2="44" stroke="#000" stroke-width="1.5"/>
    <line x1="54" y1="44" x2="66" y2="44" stroke="#000" stroke-width="1.5"/>
    <rect x="42" y="50" width="42" height="14" rx="1" fill="none" stroke="#000" stroke-width="1"/>
  </svg>`,

  'conf-table': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 98">
    <rect x="16" y="16" width="160" height="66" rx="6" fill="white" stroke="#000" stroke-width="2.5"/>
    <path d="M 32,16  Q 32,5  48,5  Q 64,5  64,16"  fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M 80,16  Q 80,5  96,5  Q 112,5 112,16" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M 128,16 Q 128,5 144,5 Q 160,5 160,16" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M 32,82  Q 32,93 48,93 Q 64,93  64,82"  fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M 80,82  Q 80,93 96,93 Q 112,93 112,82" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M 128,82 Q 128,93 144,93 Q 160,93 160,82" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M 16,28 Q 5,28  5,44 Q 5,60  16,60" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
    <path d="M 176,28 Q 187,28 187,44 Q 187,60 176,60" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
  </svg>`,

  'office-chair': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 60">
    <circle cx="30" cy="30" r="27" fill="white" stroke="#000" stroke-width="2"/>
    <path d="M 13,24 Q 13,8 30,8 Q 47,8 47,24" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round"/>
    <line x1="8"  y1="30" x2="3"  y2="30" stroke="#000" stroke-width="2" stroke-linecap="round"/>
    <line x1="52" y1="30" x2="57" y2="30" stroke="#000" stroke-width="2" stroke-linecap="round"/>
    <circle cx="30" cy="44" r="4" fill="none" stroke="#000" stroke-width="1.5"/>
    <line x1="30" y1="48" x2="22" y2="56" stroke="#000" stroke-width="1"/>
    <line x1="30" y1="48" x2="38" y2="56" stroke="#000" stroke-width="1"/>
    <line x1="30" y1="48" x2="30" y2="58" stroke="#000" stroke-width="1"/>
    <line x1="30" y1="48" x2="20" y2="52" stroke="#000" stroke-width="1"/>
    <line x1="30" y1="48" x2="40" y2="52" stroke="#000" stroke-width="1"/>
  </svg>`,

  'reception': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 140 74">
    <rect x="2"  y="2"  width="134" height="36" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="2"  y="2"  width="36"  height="70" fill="white" stroke="#000" stroke-width="2.5"/>
    <line x1="2"  y1="30" x2="136" y2="30" stroke="#000" stroke-width="1.5"/>
    <line x1="30" y1="38" x2="30"  y2="72" stroke="#000" stroke-width="1.5"/>
  </svg>`,

  'stall-wc': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 68 94">
    <rect x="2" y="2" width="64" height="90" fill="white" stroke="#000" stroke-width="3"/>
    <line x1="2"  y1="92" x2="30" y2="92" stroke="white" stroke-width="5"/>
    <line x1="2"  y1="90" x2="2"  y2="62" stroke="#000" stroke-width="2"/>
    <rect x="2" y="62" width="28" height="4" fill="#000"/>
    <path d="M 30,66 A 28,28 0 0,1 2,94" fill="none" stroke="#000" stroke-width="1" stroke-dasharray="4,3"/>
    <rect x="14" y="10" width="40" height="16" rx="2" fill="white" stroke="#000" stroke-width="1.5"/>
    <path d="M 16,26 L 16,54 Q 16,68 34,68 Q 52,68 52,54 L 52,26 Z" fill="white" stroke="#000" stroke-width="1.5"/>
    <path d="M 20,28 L 20,54 Q 20,62 34,62 Q 48,62 48,54 L 48,28 Z" fill="rgba(200,230,255,0.2)" stroke="#000" stroke-width="1"/>
  </svg>`,

  'stall-ada': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 114">
    <rect x="2" y="2" width="110" height="110" fill="white" stroke="#000" stroke-width="3"/>
    <line x1="2"  y1="112" x2="50" y2="112" stroke="white" stroke-width="6"/>
    <line x1="2"  y1="110" x2="2"  y2="70" stroke="#000" stroke-width="2"/>
    <rect x="2" y="70" width="48" height="4" fill="#000"/>
    <path d="M 50,74 A 48,48 0 0,1 2,122" fill="none" stroke="#000" stroke-width="1" stroke-dasharray="4,3"/>
    <line x1="62" y1="14" x2="106" y2="14" stroke="#000" stroke-width="3.5" stroke-linecap="round"/>
    <line x1="106" y1="14" x2="106" y2="64" stroke="#000" stroke-width="3.5" stroke-linecap="round"/>
    <rect x="64" y="22" width="38" height="14" rx="2" fill="white" stroke="#000" stroke-width="1.5"/>
    <path d="M 66,36 L 66,62 Q 66,76 83,76 Q 100,76 100,62 L 100,36 Z" fill="white" stroke="#000" stroke-width="1.5"/>
    <path d="M 70,38 L 70,62 Q 70,70 83,70 Q 96,70 96,62 L 96,38 Z" fill="rgba(200,230,255,0.2)" stroke="#000" stroke-width="1"/>
    <circle cx="34" cy="80" r="28" fill="none" stroke="#000" stroke-width="1" stroke-dasharray="5,4"/>
  </svg>`,

  'server-rack': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 90">
    <rect x="2" y="2" width="44" height="86" fill="white" stroke="#000" stroke-width="2.5"/>
    <rect x="2" y="2" width="6"  height="86" fill="none" stroke="#000" stroke-width="1"/>
    <rect x="40" y="2" width="6" height="86" fill="none" stroke="#000" stroke-width="1"/>
    <line x1="2"  y1="12" x2="46" y2="12" stroke="#000" stroke-width="1"/>
    <line x1="2"  y1="22" x2="46" y2="22" stroke="#000" stroke-width="1"/>
    <line x1="2"  y1="32" x2="46" y2="32" stroke="#000" stroke-width="1"/>
    <line x1="2"  y1="42" x2="46" y2="42" stroke="#000" stroke-width="1"/>
    <line x1="2"  y1="52" x2="46" y2="52" stroke="#000" stroke-width="1"/>
    <line x1="2"  y1="62" x2="46" y2="62" stroke="#000" stroke-width="1"/>
    <line x1="2"  y1="72" x2="46" y2="72" stroke="#000" stroke-width="1"/>
    <circle cx="41" cy="7"  r="2" fill="#000"/>
    <circle cx="41" cy="17" r="2" fill="#000"/>
    <circle cx="41" cy="27" r="2" fill="#000"/>
  </svg>`,

  // ══ STRUCTURAL & ANNOTATIONS ════════════════════════════════════════════

  'north-arrow': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 46 68">
    <line x1="23" y1="50" x2="23" y2="14" stroke="#000" stroke-width="2"/>
    <path d="M 23,2 L 31,26 L 23,20 Z" fill="#000"/>
    <path d="M 23,2 L 15,26 L 23,20 Z" fill="none" stroke="#000" stroke-width="2"/>
    <circle cx="23" cy="50" r="17" fill="white" stroke="#000" stroke-width="2"/>
    <text x="23" y="55" font-family="Arial" font-size="14" font-weight="bold" text-anchor="middle" fill="#000">N</text>
  </svg>`,

  'column-sq': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36">
    <rect x="2" y="2" width="32" height="32" fill="#000"/>
    <line x1="2"  y1="10" x2="10" y2="2"  stroke="white" stroke-width="1"/>
    <line x1="2"  y1="20" x2="20" y2="2"  stroke="white" stroke-width="1"/>
    <line x1="2"  y1="30" x2="30" y2="2"  stroke="white" stroke-width="1"/>
    <line x1="10" y1="34" x2="34" y2="10" stroke="white" stroke-width="1"/>
    <line x1="20" y1="34" x2="34" y2="20" stroke="white" stroke-width="1"/>
    <line x1="30" y1="34" x2="34" y2="30" stroke="white" stroke-width="1"/>
  </svg>`,

  'column-rd': `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36">
    <circle cx="18" cy="18" r="16" fill="#000"/>
    <line x1="10" y1="6"  x2="6"  y2="10" stroke="white" stroke-width="1"/>
    <line x1="18" y1="4"  x2="4"  y2="18" stroke="white" stroke-width="1"/>
    <line x1="26" y1="4"  x2="4"  y2="26" stroke="white" stroke-width="1"/>
    <line x1="32" y1="8"  x2="8"  y2="32" stroke="white" stroke-width="1"/>
    <line x1="32" y1="18" x2="18" y2="32" stroke="white" stroke-width="1"/>
    <line x1="32" y1="26" x2="26" y2="32" stroke="white" stroke-width="1"/>
  </svg>`,

};

export interface ArchSymbol {
  id: string;
  label: string;
}

export interface SymbolCategory {
  id: string;
  label: string;
  icon: string;
  open: boolean;
  symbols: ArchSymbol[];
}

// ── Component ──────────────────────────────────────────────────────────────

@Component({
  selector: 'app-design-editor',
  templateUrl: './design-editor.component.html',
  styleUrls: ['./design-editor.component.css']
})
export class DesignEditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('canvasEl', { static: true }) canvasEl!: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasWrapper') canvasWrapper!: ElementRef<HTMLDivElement>;

  canvas!: fabric.Canvas;

  // ── Tool state ──────────────────────────────────────────────────
  activeTool = 'select';
  strokeColor = '#1d3557';
  fillColor = 'transparent';
  strokeWidth = 2;
  fontSize = 20;
  wallThickness = 20; // pixels (represents ~8" at default scale)

  // ── Grid / scale ────────────────────────────────────────────────
  showGrid  = true;
  snapEnabled = true;
  gridSize  = 20;         // pixels per grid cell
  scalePerCell = 1;       // feet per grid cell
  scaleUnit = 'ft';

  // ── Design meta ─────────────────────────────────────────────────
  designName = 'Untitled Design';
  designDesc = '';

  // ── UI state ────────────────────────────────────────────────────
  saving = false;
  saved  = false;
  loading = true;
  symbolPanelOpen = true;

  // ── Cursor position ─────────────────────────────────────────────
  cursorX = 0;
  cursorY = 0;

  private currentDesign: Design | null = null;
  private history: string[] = [];
  private histIdx = -1;

  // Drawing tracking
  private isDown = false;
  private origX = 0;
  private origY = 0;
  private activeShape: any = null;

  // Pan tracking
  private isPanning = false;
  private lastPosX = 0;
  private lastPosY = 0;

  // Dimension tool
  dimStart: { x: number; y: number } | null = null;
  private dimPreview: fabric.Line | null = null;

  // ── Computed ────────────────────────────────────────────────────
  get canUndo() { return this.histIdx > 0; }
  get canRedo() { return this.histIdx < this.history.length - 1; }
  get zoom()    { return this.canvas ? Math.round(this.canvas.getZoom() * 100) : 100; }

  // ── Tool list ───────────────────────────────────────────────────
  readonly drawingTools = [
    { id: 'select',  icon: 'near_me',                 label: 'Select (V)'    },
    { id: 'pan',     icon: 'pan_tool',                 label: 'Pan (H)'       },
    { id: 'wall',    icon: 'horizontal_rule',          label: 'Wall (W)'      },
    { id: 'pen',     icon: 'gesture',                  label: 'Draw (P)'      },
    { id: 'line',    icon: 'timeline',                 label: 'Line (L)'      },
    { id: 'rect',    icon: 'crop_square',              label: 'Rectangle (R)' },
    { id: 'circle',  icon: 'radio_button_unchecked',   label: 'Circle (C)'   },
    { id: 'text',    icon: 'title',                    label: 'Text (T)'      },
    { id: 'dim',     icon: 'straighten',               label: 'Dimension (D)' },
    { id: 'room',    icon: 'meeting_room',             label: 'Room Label (M)'},
  ];

  // ── Symbol library ──────────────────────────────────────────────
  readonly symbolCategories: SymbolCategory[] = [
    {
      id: 'openings', label: 'Doors & Windows', icon: 'door_front', open: true,
      symbols: [
        { id: 'door-r',      label: 'Door →'      },
        { id: 'door-l',      label: 'Door ←'      },
        { id: 'door-double', label: 'Double Door'  },
        { id: 'door-sliding',label: 'Sliding'      },
        { id: 'door-pocket', label: 'Pocket'       },
        { id: 'door-garage', label: 'Garage Door'  },
        { id: 'window',      label: 'Window'       },
        { id: 'window-bay',  label: 'Bay Window'   },
      ]
    },
    {
      id: 'stairs', label: 'Stairs & Vertical', icon: 'stairs', open: false,
      symbols: [
        { id: 'stairs-up',  label: 'Stairs ↑'  },
        { id: 'stairs-l',   label: 'L-Stairs'  },
        { id: 'elevator',   label: 'Elevator'  },
        { id: 'ramp',       label: 'Ramp'      },
      ]
    },
    {
      id: 'plumbing', label: 'Plumbing', icon: 'water_drop', open: false,
      symbols: [
        { id: 'toilet',     label: 'Toilet'     },
        { id: 'sink-round', label: 'Sink'        },
        { id: 'sink-rect',  label: 'Lavatory'   },
        { id: 'bathtub',    label: 'Bathtub'    },
        { id: 'shower',     label: 'Shower'     },
        { id: 'urinal',     label: 'Urinal'     },
      ]
    },
    {
      id: 'kitchen', label: 'Kitchen', icon: 'kitchen', open: false,
      symbols: [
        { id: 'fridge',       label: 'Refrigerator' },
        { id: 'range',        label: 'Range / Stove' },
        { id: 'dishwasher',   label: 'Dishwasher'   },
        { id: 'sink-kitchen', label: 'Kitchen Sink'  },
        { id: 'counter',      label: 'Counter'      },
        { id: 'island',       label: 'Island'       },
      ]
    },
    {
      id: 'electrical', label: 'Electrical', icon: 'electrical_services', open: false,
      symbols: [
        { id: 'outlet',        label: 'Outlet'         },
        { id: 'outlet-gfi',   label: 'GFI Outlet'     },
        { id: 'switch',       label: 'Switch'          },
        { id: 'light-ceiling',label: 'Ceiling Light'   },
        { id: 'light-recessed',label:'Recessed Light'  },
        { id: 'fan',          label: 'Ceiling Fan'     },
        { id: 'smoke',        label: 'Smoke Detector'  },
      ]
    },
    {
      id: 'residential', label: 'Residential', icon: 'home', open: false,
      symbols: [
        { id: 'bed-twin',   label: 'Twin Bed'    },
        { id: 'bed-queen',  label: 'Queen Bed'   },
        { id: 'bed-king',   label: 'King Bed'    },
        { id: 'sofa',       label: 'Sofa'        },
        { id: 'chair',      label: 'Chair'       },
        { id: 'desk',       label: 'Desk'        },
        { id: 'table-round',label: 'Dining Table'},
        { id: 'fireplace',  label: 'Fireplace'   },
      ]
    },
    {
      id: 'commercial', label: 'Commercial', icon: 'business', open: false,
      symbols: [
        { id: 'office-desk',  label: 'Office Desk'    },
        { id: 'conf-table',   label: 'Conf. Table'    },
        { id: 'office-chair', label: 'Office Chair'   },
        { id: 'reception',    label: 'Reception Desk' },
        { id: 'stall-wc',     label: 'WC Stall'       },
        { id: 'stall-ada',    label: 'ADA Stall'      },
        { id: 'server-rack',  label: 'Server Rack'    },
      ]
    },
    {
      id: 'structural', label: 'Structural', icon: 'foundation', open: false,
      symbols: [
        { id: 'column-sq', label: 'Column (Square)' },
        { id: 'column-rd', label: 'Column (Round)'  },
        { id: 'north-arrow',label: 'North Arrow'    },
      ]
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private designService: DesignService,
    private authService: AuthService
  ) {}

  // ── Lifecycle ────────────────────────────────────────────────────

  ngOnInit() {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam && idParam !== 'new') {
      this.designService.getDesign(+idParam).subscribe({
        next: (d) => {
          this.currentDesign = d;
          this.designName = d.name;
          this.designDesc = d.description ?? '';
          if (this.canvas && d.canvasData) this.loadCanvas(d.canvasData);
          this.loading = false;
        },
        error: () => { this.loading = false; }
      });
    } else {
      this.loading = false;
    }
  }

  ngAfterViewInit() {
    const wrapper = this.canvasWrapper?.nativeElement;
    const w = wrapper?.clientWidth  || window.innerWidth  - 72 - 260;
    const h = wrapper?.clientHeight || window.innerHeight - 100;

    this.canvas = new fabric.Canvas(this.canvasEl.nativeElement, {
      width: w, height: h,
      backgroundColor: '#ffffff',
      selection: true,
      preserveObjectStacking: true,
    });

    this.drawGrid();
    this.setupEvents();
    this.saveState();

    if (this.currentDesign?.canvasData) this.loadCanvas(this.currentDesign.canvasData);
  }

  ngOnDestroy() {
    if (this.canvas) this.canvas.dispose();
  }

  // ── Grid ─────────────────────────────────────────────────────────

  drawGrid() {
    if (!this.showGrid) {
      this.canvas.setBackgroundColor('#ffffff', () => this.canvas.renderAll());
      return;
    }
    const gs = this.gridSize;
    const gc = document.createElement('canvas');
    gc.width  = gs;
    gc.height = gs;
    const ctx = gc.getContext('2d')!;

    // Major grid every 5 cells
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth   = 0.3;
    ctx.beginPath();
    ctx.moveTo(gs - 0.5, 0); ctx.lineTo(gs - 0.5, gs);
    ctx.moveTo(0, gs - 0.5); ctx.lineTo(gs, gs - 0.5);
    ctx.stroke();

    this.canvas.setBackgroundColor(
      { source: gc.toDataURL(), repeat: 'repeat' } as any,
      () => this.canvas.renderAll()
    );
  }

  toggleGrid() {
    this.showGrid = !this.showGrid;
    this.drawGrid();
  }

  snap(v: number): number {
    return this.snapEnabled ? Math.round(v / this.gridSize) * this.gridSize : v;
  }

  // ── Canvas events ────────────────────────────────────────────────

  setupEvents() {
    this.canvas.on('mouse:down',  (o) => this.onMouseDown(o));
    this.canvas.on('mouse:move',  (o) => this.onMouseMove(o));
    this.canvas.on('mouse:up',    (o) => this.onMouseUp(o));
    this.canvas.on('mouse:wheel', (o) => this.onWheel(o));
    this.canvas.on('object:modified', (e) => {
      if (this.snapEnabled && e.target) {
        e.target.set({ left: this.snap(e.target.left!), top: this.snap(e.target.top!) });
        e.target.setCoords();
      }
      this.saveState();
    });
    this.canvas.on('path:created',        () => this.saveState());
    this.canvas.on('text:editing:exited', () => this.saveState());
  }

  onMouseDown(o: fabric.IEvent) {
    const me = o.e as MouseEvent;
    const raw = this.canvas.getPointer(me);
    const ptr = { x: this.snap(raw.x), y: this.snap(raw.y) };

    // Update cursor display
    this.cursorX = Math.round(raw.x / this.gridSize * this.scalePerCell * 10) / 10;
    this.cursorY = Math.round(raw.y / this.gridSize * this.scalePerCell * 10) / 10;

    this.isDown = true;
    this.origX  = ptr.x;
    this.origY  = ptr.y;

    switch (this.activeTool) {
      case 'wall':
        this.activeShape = new fabric.Rect({
          left: ptr.x, top: ptr.y - this.wallThickness / 2,
          width: 1, height: this.wallThickness,
          fill: '#1e293b', stroke: '#1e293b', strokeWidth: 0,
          selectable: false, angle: 0,
        });
        this.canvas.add(this.activeShape);
        break;

      case 'line':
        this.activeShape = new fabric.Line(
          [ptr.x, ptr.y, ptr.x, ptr.y],
          { stroke: this.strokeColor, strokeWidth: this.strokeWidth, selectable: false, evented: false }
        );
        this.canvas.add(this.activeShape);
        break;

      case 'rect':
        this.activeShape = new fabric.Rect({
          left: ptr.x, top: ptr.y, width: 1, height: 1,
          fill: this.fillColor, stroke: this.strokeColor, strokeWidth: this.strokeWidth, selectable: false,
        });
        this.canvas.add(this.activeShape);
        break;

      case 'circle':
        this.activeShape = new fabric.Ellipse({
          left: ptr.x, top: ptr.y, rx: 1, ry: 1,
          fill: this.fillColor, stroke: this.strokeColor, strokeWidth: this.strokeWidth, selectable: false,
        });
        this.canvas.add(this.activeShape);
        break;

      case 'text':
        const txt = new fabric.IText('Label', {
          left: ptr.x, top: ptr.y,
          fontSize: this.fontSize, fill: this.strokeColor, fontFamily: 'Arial',
        });
        this.canvas.add(txt);
        this.canvas.setActiveObject(txt);
        txt.enterEditing();
        txt.selectAll();
        this.setTool('select');
        this.saveState();
        this.isDown = false;
        break;

      case 'room':
        const roomLabel = new fabric.IText('Room Name', {
          left: ptr.x, top: ptr.y,
          fontSize: 18, fill: '#1d3557', fontFamily: 'Arial',
          fontWeight: 'bold', textAlign: 'center',
        });
        this.canvas.add(roomLabel);
        this.canvas.setActiveObject(roomLabel);
        roomLabel.enterEditing();
        roomLabel.selectAll();
        this.setTool('select');
        this.saveState();
        this.isDown = false;
        break;

      case 'dim':
        if (!this.dimStart) {
          // First click — record start
          this.dimStart = { x: ptr.x, y: ptr.y };
          this.dimPreview = new fabric.Line([ptr.x, ptr.y, ptr.x, ptr.y], {
            stroke: '#3b82f6', strokeWidth: 1.5, strokeDashArray: [5, 3],
            selectable: false, evented: false,
          });
          this.canvas.add(this.dimPreview);
        } else {
          // Second click — finalize dimension
          this.drawDimension(this.dimStart, ptr);
          if (this.dimPreview) { this.canvas.remove(this.dimPreview); this.dimPreview = null; }
          this.dimStart = null;
        }
        this.isDown = false;
        break;

      case 'pan':
        this.isPanning = true;
        this.canvas.selection = false;
        this.lastPosX = me.clientX;
        this.lastPosY = me.clientY;
        break;
    }
  }

  onMouseMove(o: fabric.IEvent) {
    const me  = o.e as MouseEvent;
    const raw = this.canvas.getPointer(me);
    const ptr = { x: this.snap(raw.x), y: this.snap(raw.y) };

    this.cursorX = Math.round(raw.x / this.gridSize * this.scalePerCell * 10) / 10;
    this.cursorY = Math.round(raw.y / this.gridSize * this.scalePerCell * 10) / 10;

    // Dimension preview line
    if (this.activeTool === 'dim' && this.dimStart && this.dimPreview) {
      (this.dimPreview as fabric.Line).set({ x2: raw.x, y2: raw.y });
      this.canvas.renderAll();
    }

    if (!this.isDown) return;

    switch (this.activeTool) {
      case 'wall':
        if (this.activeShape) {
          const dx = ptr.x - this.origX;
          const dy = ptr.y - this.origY;
          const len = Math.sqrt(dx * dx + dy * dy);
          const ang = Math.atan2(dy, dx) * 180 / Math.PI;
          (this.activeShape as fabric.Rect).set({
            left: this.origX, top: this.origY - this.wallThickness / 2,
            width: len, height: this.wallThickness,
            angle: ang, originX: 'left', originY: 'top',
          });
          this.canvas.renderAll();
        }
        break;

      case 'line':
        (this.activeShape as fabric.Line)?.set({ x2: ptr.x, y2: ptr.y });
        this.canvas.renderAll();
        break;

      case 'rect':
        (this.activeShape as fabric.Rect)?.set({
          width:  Math.abs(this.origX - ptr.x),
          height: Math.abs(this.origY - ptr.y),
          left:   Math.min(this.origX, ptr.x),
          top:    Math.min(this.origY, ptr.y),
        });
        this.canvas.renderAll();
        break;

      case 'circle': {
        const rx = Math.abs(this.origX - ptr.x) / 2;
        const ry = Math.abs(this.origY - ptr.y) / 2;
        (this.activeShape as fabric.Ellipse)?.set({
          rx, ry,
          left: Math.min(this.origX, ptr.x),
          top:  Math.min(this.origY, ptr.y),
        });
        this.canvas.renderAll();
        break;
      }

      case 'pan':
        if (this.isPanning) {
          const vpt = this.canvas.viewportTransform!;
          vpt[4] += me.clientX - this.lastPosX;
          vpt[5] += me.clientY - this.lastPosY;
          this.canvas.requestRenderAll();
          this.lastPosX = me.clientX;
          this.lastPosY = me.clientY;
        }
        break;
    }
  }

  onMouseUp(o: fabric.IEvent) {
    if (['wall', 'line', 'rect', 'circle'].includes(this.activeTool) && this.activeShape) {
      this.activeShape.set({ selectable: true, evented: true });
      this.canvas.setActiveObject(this.activeShape);
      this.activeShape = null;
      this.saveState();
    }
    if (this.activeTool === 'pan') {
      this.isPanning = false;
      this.canvas.selection = true;
    }
    this.isDown = false;
  }

  onWheel(o: fabric.IEvent) {
    const e = o.e as WheelEvent;
    let z = this.canvas.getZoom();
    z *= 0.999 ** e.deltaY;
    z = Math.min(Math.max(z, 0.05), 10);
    this.canvas.zoomToPoint({ x: e.offsetX, y: e.offsetY }, z);
    e.preventDefault();
    e.stopPropagation();
  }

  // ── Dimension tool ───────────────────────────────────────────────

  drawDimension(p1: { x: number; y: number }, p2: { x: number; y: number }) {
    const dx   = p2.x - p1.x;
    const dy   = p2.y - p1.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const feet = dist / this.gridSize * this.scalePerCell;
    const label = feet % 1 === 0 ? `${feet}'` : `${feet.toFixed(1)}'`;
    const ang   = Math.atan2(dy, dx) * 180 / Math.PI;
    const midX  = (p1.x + p2.x) / 2;
    const midY  = (p1.y + p2.y) / 2;
    const perpR = (ang + 90) * Math.PI / 180;
    const tLen  = 10;

    const dimLine = new fabric.Line([p1.x, p1.y, p2.x, p2.y], {
      stroke: '#3b82f6', strokeWidth: 1.5,
    });
    const tick1 = new fabric.Line([
      p1.x - tLen * Math.cos(perpR), p1.y - tLen * Math.sin(perpR),
      p1.x + tLen * Math.cos(perpR), p1.y + tLen * Math.sin(perpR),
    ], { stroke: '#3b82f6', strokeWidth: 1.5 });
    const tick2 = new fabric.Line([
      p2.x - tLen * Math.cos(perpR), p2.y - tLen * Math.sin(perpR),
      p2.x + tLen * Math.cos(perpR), p2.y + tLen * Math.sin(perpR),
    ], { stroke: '#3b82f6', strokeWidth: 1.5 });
    const dimText = new fabric.Text(label, {
      left: midX, top: midY,
      originX: 'center', originY: 'center',
      angle: ang, fontSize: 14, fill: '#3b82f6', fontFamily: 'Arial',
      backgroundColor: 'rgba(255,255,255,0.85)',
    });

    const grp = new fabric.Group([dimLine, tick1, tick2, dimText], { selectable: true });
    this.canvas.add(grp);
    this.canvas.renderAll();
    this.saveState();
  }

  // ── Tool switching ────────────────────────────────────────────────

  setTool(tool: string) {
    // Cancel dim mode if switching away
    if (this.activeTool === 'dim' && tool !== 'dim') {
      if (this.dimPreview) { this.canvas.remove(this.dimPreview); this.dimPreview = null; }
      this.dimStart = null;
    }

    this.activeTool = tool;
    this.canvas.isDrawingMode = tool === 'pen';
    this.canvas.selection     = tool === 'select';
    this.canvas.discardActiveObject();

    if (tool === 'pen') {
      const b = this.canvas.freeDrawingBrush;
      b.color = this.strokeColor;
      b.width = this.strokeWidth;
    }

    const cursors: Record<string, string> = {
      select: 'default', pan: 'grab', text: 'text', room: 'text',
      wall: 'crosshair', line: 'crosshair', rect: 'crosshair',
      circle: 'crosshair', pen: 'crosshair', dim: 'crosshair',
    };
    this.canvas.defaultCursor = cursors[tool] ?? 'crosshair';
    this.canvas.renderAll();
  }

  onColorChange() {
    if (this.canvas.isDrawingMode) this.canvas.freeDrawingBrush.color = this.strokeColor;
    const obj = this.canvas.getActiveObject();
    if (obj) {
      obj.set(obj.type === 'i-text' || obj.type === 'text'
        ? { fill: this.strokeColor }
        : { stroke: this.strokeColor });
      this.canvas.renderAll();
    }
  }

  onWidthChange() {
    if (this.canvas.isDrawingMode) this.canvas.freeDrawingBrush.width = this.strokeWidth;
    const obj = this.canvas.getActiveObject();
    if (obj) { obj.set({ strokeWidth: this.strokeWidth }); this.canvas.renderAll(); }
  }

  // ── Symbol placement ──────────────────────────────────────────────

  placeSymbol(sym: ArchSymbol) {
    const svgStr = SVGS[sym.id];
    if (!svgStr) return;

    fabric.loadSVGFromString(svgStr, (objects: fabric.Object[], options: any) => {
      const grp = fabric.util.groupSVGElements(objects, options);

      // Center in current viewport
      const vpt  = this.canvas.viewportTransform!;
      const zoom = this.canvas.getZoom();
      const cx   = (this.canvas.width!  / 2 - vpt[4]) / zoom;
      const cy   = (this.canvas.height! / 2 - vpt[5]) / zoom;

      grp.set({
        left: this.snap(cx - (grp.width  ?? 40) / 2),
        top:  this.snap(cy - (grp.height ?? 40) / 2),
        data: { symbolId: sym.id, symbolLabel: sym.label },
      });

      this.canvas.add(grp);
      this.canvas.setActiveObject(grp);
      this.canvas.renderAll();
      this.saveState();
      this.setTool('select');
    });
  }

  getSymbolSrc(id: string): string {
    const svg = SVGS[id];
    if (!svg) return '';
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  }

  // ── History ───────────────────────────────────────────────────────

  saveState() {
    const json = JSON.stringify(this.canvas.toJSON());
    this.history = this.history.slice(0, this.histIdx + 1);
    this.history.push(json);
    this.histIdx++;
    if (this.history.length > 60) { this.history.shift(); this.histIdx--; }
  }

  undo() {
    if (!this.canUndo) return;
    this.histIdx--;
    this.canvas.loadFromJSON(JSON.parse(this.history[this.histIdx]), () => this.canvas.renderAll());
  }

  redo() {
    if (!this.canRedo) return;
    this.histIdx++;
    this.canvas.loadFromJSON(JSON.parse(this.history[this.histIdx]), () => this.canvas.renderAll());
  }

  // ── Object actions ────────────────────────────────────────────────

  deleteSelected() {
    const objs = this.canvas.getActiveObjects();
    if (!objs.length) return;
    this.canvas.discardActiveObject();
    objs.forEach(o => this.canvas.remove(o));
    this.canvas.renderAll();
    this.saveState();
  }

  clearAll() {
    if (!confirm('Clear all objects from the canvas?')) return;
    this.canvas.clear();
    this.drawGrid();
    this.saveState();
  }

  bringForward()  { const o = this.canvas.getActiveObject(); if (o) { this.canvas.bringForward(o);  this.canvas.renderAll(); this.saveState(); } }
  sendBackward()  { const o = this.canvas.getActiveObject(); if (o) { this.canvas.sendBackwards(o); this.canvas.renderAll(); this.saveState(); } }

  // ── Background image ──────────────────────────────────────────────

  uploadBackground(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      fabric.Image.fromURL(ev.target!.result as string, (img) => {
        const scale = Math.min(
          this.canvas.width!  / (img.width  ?? 1),
          this.canvas.height! / (img.height ?? 1), 1
        );
        this.canvas.setBackgroundImage(img, () => {
          img.scale(scale); this.canvas.renderAll(); this.saveState();
        });
      });
    };
    reader.readAsDataURL(file);
  }

  removeBackground() {
    (this.canvas as any).backgroundImage = null;
    this.drawGrid();
    this.saveState();
  }

  // ── Zoom ──────────────────────────────────────────────────────────

  zoomIn()    { this.setZoom(this.canvas.getZoom() * 1.2); }
  zoomOut()   { this.setZoom(this.canvas.getZoom() / 1.2); }
  resetZoom() { this.canvas.setViewportTransform([1,0,0,1,0,0]); this.canvas.renderAll(); }

  private setZoom(z: number) {
    z = Math.min(Math.max(z, 0.05), 10);
    this.canvas.zoomToPoint({ x: this.canvas.width! / 2, y: this.canvas.height! / 2 }, z);
  }

  // ── Load / Save ───────────────────────────────────────────────────

  loadCanvas(json: string) {
    try {
      this.canvas.loadFromJSON(JSON.parse(json), () => {
        this.drawGrid();
        this.canvas.renderAll();
        this.history = [json];
        this.histIdx = 0;
      });
    } catch {}
  }

  save() {
    this.saving = true;
    const canvasData    = JSON.stringify(this.canvas.toJSON());
    const thumbnailData = this.canvas.toDataURL({ format: 'png', multiplier: 0.25, quality: 0.7 });
    const user = this.authService.getCurrentUser();
    const now  = new Date().toISOString();

    const design: Design = {
      ...this.currentDesign,
      name:            this.designName || 'Untitled Design',
      description:     this.designDesc,
      canvasData, thumbnailData,
      createdByUserId: this.currentDesign?.createdByUserId ?? String(user?.id ?? ''),
      createdAt:       this.currentDesign?.createdAt ?? now,
      updatedAt:       now,
    };

    const isNew = !this.currentDesign?.id;
    const call$ = isNew
      ? this.designService.createDesign(design)
      : this.designService.updateDesign(+this.currentDesign!.id, design);

    call$.subscribe({
      next: (saved) => {
        this.currentDesign = saved;
        this.saving = false; this.saved = true;
        if (isNew) this.router.navigate(['/designs', saved.id], { replaceUrl: true });
        setTimeout(() => this.saved = false, 3000);
      },
      error: () => { this.saving = false; }
    });
  }

  exportPng() {
    const link = document.createElement('a');
    link.download = `${this.designName || 'design'}.png`;
    link.href = this.canvas.toDataURL({ format: 'png', multiplier: 1 });
    link.click();
  }

  goBack() { this.router.navigate(['/designs']); }

  // ── Keyboard shortcuts ────────────────────────────────────────────

  @HostListener('window:keydown', ['$event'])
  onKey(e: KeyboardEvent) {
    const tag = (document.activeElement as HTMLElement)?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;

    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'z') { e.preventDefault(); this.undo(); }
      if (e.key === 'y') { e.preventDefault(); this.redo(); }
      if (e.key === 's') { e.preventDefault(); this.save(); }
    } else {
      const map: Record<string, string> = {
        v:'select', h:'pan', w:'wall', p:'pen', l:'line',
        r:'rect',   c:'circle', t:'text', d:'dim', m:'room',
      };
      if (map[e.key]) this.setTool(map[e.key]);
      if (e.key === 'Delete' || e.key === 'Backspace') this.deleteSelected();
      if (e.key === 'Escape') {
        this.dimStart = null;
        if (this.dimPreview) { this.canvas.remove(this.dimPreview); this.dimPreview = null; }
        this.setTool('select');
      }
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (!this.canvas || !this.canvasWrapper) return;
    this.canvas.setWidth(this.canvasWrapper.nativeElement.clientWidth);
    this.canvas.setHeight(this.canvasWrapper.nativeElement.clientHeight);
    this.canvas.renderAll();
  }
}
